import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { getProducts, getCategories } from "../api/woocommerce";
import { wpApi } from "../api/axios";
import ProductCard from "../components/ui/ProductCard";
import Spinner from "../components/ui/Spinner";

export default function Home() {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [categories, setCategories] = useState([]);
    const [slides, setSlides] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            try {
                const [featuredRes, newRes, catsRes, slidesRes] =
                    await Promise.all([
                        getProducts({ featured: true, perPage: 4 }),
                        getProducts({
                            perPage: 8,
                            orderby: "date",
                            order: "desc",
                        }),
                        getCategories({ perPage: 6, hideEmpty: true }),
                        wpApi
                            .get("/headless/v1/hero-slides")
                            .then((r) => r.data)
                            .catch(() => []),
                    ]);
                setFeaturedProducts(featuredRes.products);
                setNewArrivals(newRes.products);
                setCategories(catsRes);
                if (Array.isArray(slidesRes) && slidesRes.length > 0) {
                    setSlides(slidesRes);
                }
            } catch (err) {
                console.error("Failed to load homepage data:", err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    if (loading) return <Spinner fullPage />;

    return (
        <div>
            {/* ── Hero Slider ── */}
            {slides && slides.length > 0 && <HeroSlider slides={slides} />}

            {/* ── Trust Bar ── */}
            <TrustBar />

            {/* ── Categories ── */}
            {categories.length > 0 && (
                <CategoriesSection categories={categories} />
            )}

            {/* ── Featured Products ── */}
            {featuredProducts.length > 0 && (
                <ProductSection
                    title="Featured Products"
                    subtitle="Hand-picked just for you"
                    products={featuredProducts}
                />
            )}

            {/* ── Why FitsAura ── */}
            <WhyChooseUs />

            {/* ── Promo Banners ── */}
            <PromoSection />

            {/* ── New Arrivals ── */}
            {newArrivals.length > 0 && (
                <ProductSection
                    title="New Arrivals"
                    subtitle="Fresh in from our collection"
                    products={newArrivals}
                    viewAll="/shop"
                />
            )}

            {/* ── Testimonials ── */}
            <TestimonialsSection />

            {/* ── Newsletter ── */}
            <NewsletterSection />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// HERO SLIDER
// ═══════════════════════════════════════════════════════════════════

function HeroSlider({ slides }) {
    if (!slides || slides.length === 0) return null;

    return (
        <section className="relative min-h-[280px] sm:min-h-[400px] lg:min-h-[520px]">
            <Swiper
                modules={[Autoplay, Pagination]}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                pagination={{
                    clickable: true,
                    renderBullet: (index, className) =>
                        `<span class="${className} !w-3 !h-3 !rounded-full !bg-white/40 !opacity-100 [&.swiper-pagination-bullet-active]:!bg-white [&.swiper-pagination-bullet-active]:!w-6 [&.swiper-pagination-bullet-active]:!rounded-full"></span>`,
                }}
                loop={true}
                speed={700}
                className="w-full swiper-hero min-h-[280px] sm:min-h-[400px] lg:min-h-[520px]"
            >
                {slides.map((slide, i) => (
                    <SwiperSlide key={i}>
                        <div className="relative text-white overflow-hidden min-h-[280px] sm:min-h-[400px] lg:min-h-[520px]">
                            {/* Background Image */}
                            <img
                                src={slide.bgImage}
                                alt=""
                                fetchPriority={i === 0 ? "high" : "low"}
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{
                                    objectPosition:
                                        slide.bgPosition || "center",
                                }}
                            />
                            {/* Fallback dark overlay + gradient overlay */}
                            <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                            {slide.gradient && (
                                <div
                                    className={`absolute inset-0 bg-gradient-to-br pointer-events-none ${slide.gradient}`}
                                />
                            )}
                            {/* Decorative circles */}
                            <div className="absolute inset-0 opacity-[0.07] z-10">
                                <div className="absolute -top-32 -right-20 w-[500px] h-[500px] border-[30px] border-white rounded-full" />
                                <div className="absolute -bottom-40 -left-20 w-[600px] h-[600px] border-[30px] border-white rounded-full" />
                                <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-white rounded-full" />
                                <div className="absolute top-1/4 right-1/3 w-2 h-2 bg-white/60 rounded-full" />
                                <div className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-white/60 rounded-full" />
                            </div>

                            <div className="container relative z-20 py-20 md:py-28 lg:py-36">
                                <div className="max-w-2xl mx-auto md:mx-0 text-center md:text-left">
                                    <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 text-white text-xs font-semibold tracking-widest uppercase mb-5 backdrop-blur-sm border border-white/10">
                                        {slide.badge}
                                    </span>
                                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight mb-4">
                                        <span
                                            className={
                                                slide.titleClass || "text-white"
                                            }
                                        >
                                            {slide.title}
                                        </span>{" "}
                                        <br />
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-amber-300">
                                            {slide.highlight}
                                        </span>
                                    </h1>
                                    <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-lg mb-8 mx-auto md:mx-0">
                                        {slide.description}
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-3 items-center sm:justify-center md:justify-start">
                                        <Link
                                            to={slide.link}
                                            className="inline-flex items-center justify-center gap-2 bg-white text-neutral-900 font-bold px-8 py-3.5 rounded-xl hover:bg-neutral-100 transition-all duration-200 shadow-xl shadow-black/10 hover:shadow-2xl hover:shadow-black/20 hover:-translate-y-0.5 w-full sm:w-auto"
                                        >
                                            {slide.cta}
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-4 h-4 shrink-0"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2.5}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                                />
                                            </svg>
                                        </Link>
                                        <Link
                                            to="/shop"
                                            className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-200 w-full sm:w-auto"
                                        >
                                            Browse Categories
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// TRUST BAR
// ═══════════════════════════════════════════════════════════════════

const trustItems = [
    {
        icon: (
            <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                />
            </svg>
        ),
        title: "Free Shipping",
        subtitle: "On orders over AED 50",
    },
    {
        icon: (
            <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
            </svg>
        ),
        title: "Secure Checkout",
        subtitle: "SSL encrypted payments",
    },
    {
        icon: (
            <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
                />
            </svg>
        ),
        title: "30-Day Returns",
        subtitle: "Easy, hassle-free policy",
    },
    {
        icon: (
            <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
            </svg>
        ),
        title: "Expert Support",
        subtitle: "We're here to help",
    },
];

function TrustBar() {
    return (
        <section className="bg-white border-b border-neutral-100">
            <div className="container">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
                    {trustItems.map((item) => (
                        <div
                            key={item.title}
                            className="flex items-center gap-3 justify-center md:justify-start"
                        >
                            <div className="w-12 h-12 rounded-xl bg-secondary-50 text-secondary-600 flex items-center justify-center shrink-0">
                                {item.icon}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-neutral-800">
                                    {item.title}
                                </p>
                                <p className="text-xs text-neutral-400">
                                    {item.subtitle}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════════

function CategoriesSection({ categories }) {
    return (
        <section className="py-16 md:py-20 bg-neutral-50">
            <div className="container">
                <div className="text-center mb-10">
                    <span className="text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-2 block">
                        Shop by Category
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-3">
                        Browse by Category
                    </h2>
                    <p className="text-neutral-500 max-w-md mx-auto">
                        From everyday essentials to standout statement pieces —
                        find exactly what your wardrobe needs.
                    </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 md:gap-5">
                    {categories.map((cat) => (
                        <Link
                            key={cat.id}
                            to={`/category/${cat.slug}`}
                            className="group relative flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-white border border-neutral-100 hover:border-secondary-200 hover:shadow-lg hover:shadow-secondary-500/5 transition-all duration-300"
                        >
                            {/* Icon placeholder */}
                            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-secondary-50 to-secondary-100 flex items-center justify-center overflow-hidden group-hover:from-secondary-100 group-hover:to-secondary-200 group-hover:scale-105 transition-all duration-300">
                                {cat.image ? (
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-7 h-7 text-secondary-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={1.5}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"
                                        />
                                    </svg>
                                )}
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-bold text-neutral-800 group-hover:text-secondary-700 transition-colors leading-tight">
                                    {cat.name}
                                </p>
                                <p className="text-xs text-neutral-400 mt-0.5 font-medium">
                                    {cat.count}{" "}
                                    {cat.count === 1 ? "item" : "items"}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PRODUCT GRID SECTION
// ═══════════════════════════════════════════════════════════════════

function ProductSection({
    title,
    subtitle,
    products,
    viewAll,
    bg = "bg-white",
}) {
    return (
        <section className={`py-16 md:py-20 ${bg}`}>
            <div className="container">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-2">
                            {title}
                        </h2>
                        <p className="text-neutral-500">{subtitle}</p>
                    </div>
                    {viewAll && (
                        <Link
                            to={viewAll}
                            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-secondary-600 hover:text-secondary-700 transition-colors shrink-0 group"
                        >
                            View All
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </Link>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>
                {viewAll && (
                    <div className="text-center mt-8 sm:hidden">
                        <Link
                            to={viewAll}
                            className="inline-flex items-center gap-1.5 text-sm font-bold text-secondary-600 hover:text-secondary-700 transition-colors"
                        >
                            View All Products
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// WHY CHOOSE US
// ═══════════════════════════════════════════════════════════════════

const reasons = [
    {
        icon: (
            <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
            </svg>
        ),
        title: "Premium Quality",
        description:
            "Every piece is carefully curated for quality and craftsmanship. From fabric to finish — we only stock what we'd wear ourselves.",
    },
    {
        icon: (
            <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        ),
        title: "Fast Delivery",
        description:
            "We process and ship orders within 24 hours. Most deliveries arrive within 3-5 business days anywhere in the UAE.",
    },
    {
        icon: (
            <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                />
            </svg>
        ),
        title: "Sustainable Choices",
        description:
            "We're committed to eco-friendly packaging and partner with brands that prioritize ethical production and sustainable materials.",
    },
    {
        icon: (
            <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                />
            </svg>
        ),
        title: "Style Inspiration",
        description:
            "Join a community of fashion enthusiasts. Get inspired by curated looks, styling tips, and trends shared by our team and customers.",
    },
];

function WhyChooseUs() {
    return (
        <section className="py-16 md:py-20 bg-neutral-50">
            <div className="container">
                <div className="text-center mb-14">
                    <span className="text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-2 block">
                        Why FitsAura
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-3">
                        Why Shop With Us
                    </h2>
                    <p className="text-neutral-500 max-w-lg mx-auto">
                        We believe great style should be effortless. Quality
                        pieces, reliable service, and a shopping experience
                        you'll love.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {reasons.map((r) => (
                        <div
                            key={r.title}
                            className="bg-white rounded-2xl p-6 md:p-7 border border-neutral-100 hover:border-secondary-200 hover:shadow-xl hover:shadow-secondary-500/5 transition-all duration-300 group"
                        >
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-secondary-50 to-secondary-100 text-secondary-600 flex items-center justify-center mb-5 group-hover:from-secondary-500 group-hover:to-secondary-600 group-hover:text-white transition-all duration-300">
                                {r.icon}
                            </div>
                            <h3 className="text-lg font-bold text-neutral-900 mb-2">
                                {r.title}
                            </h3>
                            <p className="text-sm text-neutral-500 leading-relaxed">
                                {r.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// PROMO BANNERS
// ═══════════════════════════════════════════════════════════════════

function PromoSection() {
    return (
        <section className="py-16 md:py-20 bg-white">
            <div className="container">
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Promo Card 1 */}
                    <Link
                        to="/shop"
                        className="group relative rounded-2xl bg-gradient-to-br from-secondary-600 via-secondary-700 to-purple-900 text-white p-8 md:p-10 overflow-hidden hover:-translate-y-1 transition-all duration-300 hover:shadow-2xl hover:shadow-secondary-500/20"
                    >
                        <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/5 rounded-full group-hover:scale-150 transition-transform duration-700" />
                        <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full group-hover:scale-150 transition-transform duration-700 delay-100" />
                        <div className="relative">
                            <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold tracking-wider uppercase mb-4">
                                Limited Time
                            </span>
                            <h3 className="text-2xl md:text-3xl font-extrabold mb-2 leading-tight text-white">
                                Up to{" "}
                                <span className="text-yellow-300">40% Off</span>
                            </h3>
                            <p className="text-white/70 text-sm mb-6 max-w-xs">
                                On selected fitness gear and accessories. This
                                week only — don't miss out!
                            </p>
                            <span className="inline-flex items-center gap-1.5 bg-white text-secondary-700 font-bold text-sm px-6 py-3 rounded-xl group-hover:bg-neutral-100 transition-colors">
                                Shop Sale
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                                    />
                                </svg>
                            </span>
                        </div>
                    </Link>

                    {/* Promo Card 2 */}
                    <Link
                        to="/shop"
                        className="group relative rounded-2xl bg-gradient-to-br from-neutral-800 via-neutral-900 to-neutral-950 text-white p-8 md:p-10 overflow-hidden hover:-translate-y-1 transition-all duration-300 hover:shadow-2xl hover:shadow-neutral-900/20"
                    >
                        <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-white/5 rounded-full group-hover:scale-150 transition-transform duration-700" />
                        <div className="absolute -left-8 -top-8 w-32 h-32 bg-white/5 rounded-full group-hover:scale-150 transition-transform duration-700 delay-100" />
                        <div className="relative">
                            <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold tracking-wider uppercase mb-4">
                                Free Shipping
                            </span>
                            <h3 className="text-2xl md:text-3xl font-extrabold mb-2 leading-tight text-white">
                                Orders Over{" "}
                                <span className="text-emerald-400">AED 50</span>
                            </h3>
                            <p className="text-white/70 text-sm mb-6 max-w-xs">
                                No coupon needed. Free shipping applied
                                automatically at checkout. Plus easy 30-day
                                returns.
                            </p>
                            <span className="inline-flex items-center gap-1.5 bg-white text-neutral-900 font-bold text-sm px-6 py-3 rounded-xl group-hover:bg-neutral-100 transition-colors">
                                Start Shopping
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                                    />
                                </svg>
                            </span>
                        </div>
                    </Link>
                </div>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// TESTIMONIALS
// ═══════════════════════════════════════════════════════════════════

const testimonials = [
    {
        quote: "Absolutely love the quality! I ordered a handbag and a pair of shoes — both exceeded my expectations. The packaging was beautiful and delivery was faster than expected.",
        name: "Sarah Mitchell",
        location: "Dubai",
        rating: 5,
    },
    {
        quote: "FitsAura is my go-to for accessories now. The watch I bought has such elegant design and the necklace craftsmanship is stunning. Customer service was incredibly helpful with sizing too.",
        name: "Marcus Johnson",
        location: "Abu Dhabi",
        rating: 5,
    },
    {
        quote: "I was hesitant about ordering clothes online, but FitsAura made it effortless. The size guide was spot on, the fabric quality is amazing, and returns were completely hassle-free.",
        name: "Emily Chen",
        location: "Sharjah",
        rating: 5,
    },
];

function TestimonialsSection() {
    return (
        <section className="py-16 md:py-20 bg-white">
            <div className="container">
                <div className="text-center mb-12">
                    <span className="text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-2 block">
                        Testimonials
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 mb-3">
                        What Our Customers Say
                    </h2>
                    <p className="text-neutral-500 max-w-md mx-auto">
                        Real reviews from real people who trust FitsAura for
                        their everyday style.
                    </p>
                </div>

                <div className="max-w-3xl mx-auto">
                    <Swiper
                        modules={[Autoplay, Pagination]}
                        autoplay={{ delay: 5000, disableOnInteraction: false }}
                        pagination={{
                            clickable: true,
                            renderBullet: (index, className) =>
                                `<span class="${className} !w-2.5 !h-2.5 !rounded-full !bg-neutral-300 !opacity-100 [&.swiper-pagination-bullet-active]:!bg-secondary-500"></span>`,
                        }}
                        loop={true}
                        className="!pb-12"
                    >
                        {testimonials.map((t, i) => (
                            <SwiperSlide key={i}>
                                <div className="text-center px-4">
                                    {/* Quote icon */}
                                    <div className="w-12 h-12 rounded-full bg-secondary-50 text-secondary-500 flex items-center justify-center mx-auto mb-6">
                                        <svg
                                            className="w-6 h-6"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C9.591 11.69 11 13.166 11 15c0 1.933-1.567 3.5-3.5 3.5-1.172 0-2.178-.566-2.917-1.179zM15.583 17.321C14.553 16.227 14 15 14 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C20.591 11.69 22 13.166 22 15c0 1.933-1.567 3.5-3.5 3.5-1.172 0-2.178-.566-2.917-1.179z" />
                                        </svg>
                                    </div>

                                    <blockquote className="text-lg md:text-xl text-neutral-700 leading-relaxed mb-6 font-medium italic">
                                        &ldquo;{t.quote}&rdquo;
                                    </blockquote>

                                    {/* Stars */}
                                    <div className="flex items-center justify-center gap-1 mb-3">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <svg
                                                key={s}
                                                className={`w-5 h-5 ${
                                                    s <= t.rating
                                                        ? "text-amber-400"
                                                        : "text-neutral-200"
                                                }`}
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        ))}
                                    </div>

                                    <div>
                                        <p className="font-bold text-neutral-900">
                                            {t.name}
                                        </p>
                                        <p className="text-sm text-neutral-400">
                                            {t.location}
                                        </p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </section>
    );
}

// ═══════════════════════════════════════════════════════════════════
// NEWSLETTER
// ═══════════════════════════════════════════════════════════════════

function NewsletterSection() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | success | error

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) return;
        setStatus("loading");

        // Simulate subscription — in production, call an API endpoint
        setTimeout(() => {
            setStatus("success");
            setEmail("");
            setTimeout(() => setStatus("idle"), 4000);
        }, 1000);
    };

    return (
        <section className="py-16 md:py-20 bg-gradient-to-br from-secondary-600 via-secondary-700 to-purple-900">
            <div className="container">
                <div className="max-w-xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
                        Stay in the Loop
                    </h2>
                    <p className="text-white/70 mb-8 leading-relaxed">
                        Be the first to know about new arrivals, exclusive
                        offers, and curated style inspiration. No spam — we
                        promise.
                    </p>

                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                    >
                        <div className="flex-1 relative">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="w-full px-5 py-3.5 rounded-xl bg-white/15 border border-white/20 text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/20 transition-all"
                                disabled={
                                    status === "loading" || status === "success"
                                }
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={
                                status === "loading" || status === "success"
                            }
                            className="px-7 py-3.5 rounded-xl bg-white text-secondary-700 font-bold text-sm hover:bg-neutral-100 transition-all disabled:opacity-70 shadow-xl shadow-black/10 shrink-0"
                        >
                            {status === "loading" ? (
                                <span className="flex items-center gap-2">
                                    <span className="w-4 h-4 border-2 border-secondary-600 border-t-transparent rounded-full animate-spin" />
                                    Sending...
                                </span>
                            ) : status === "success" ? (
                                <span className="flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2.5}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                    Subscribed!
                                </span>
                            ) : (
                                "Subscribe"
                            )}
                        </button>
                    </form>

                    {status === "success" && (
                        <p className="text-emerald-300 text-sm mt-3 font-medium">
                            ✅ You're in! Welcome to the FitsAura style club.
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}
